import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import { TaskService } from "../services";
import TasksFiltersVisualStore from "./TasksFiltersVisualStore";
import {
    ErrorStore,
    NotificationStore,
    ProjectsStore,
    TabsStore,
    UserProfileStore,
} from "../../common/stores";
import { ALL_PROJECTS } from "../screens/TasksPage";
import {
    MetadataPushMessage,
    TaskListModel,
    TaskModel,
    TaskUpdateFieldName,
    TasksData,
} from "../types";
import { MetadataDefinition } from "../../administration/types/Metadata";
import { GlobalAdministrationService } from "../../administration/services/GlobalAdministrationService";
import {
    ColumnsFilter,
    PinnedTask,
    UserProfile,
} from "../../common/services/types";
import TasksGridVisualStore from "./TasksGridVisualStore";
import TaskCreateVisualStore from "./TaskCreateVisualStore";
import TaskViewVisualStore from "./TaskViewVisualStore";
import { TaskStatus } from "../../task_statuses/types";
import { ActionService } from "../../administration/services/ActionService";
import { ActionDefinition } from "../../administration/types/Actions";
import ClientActionRunner from "../../administration/misc/ClientActionHandler";
import { TaskType } from "../../task_types/types";
import TaskTypesService from "../../task_types/services/TaskTypesService";
import { ResultApi } from "../../common/services/AppClient";

export default class TasksRootStore {
    tasksGlobalViewVisualStore: TaskViewVisualStore;

    tasksFiltersVisualStore: TasksFiltersVisualStore;

    tasksGridVisualStore: TasksGridVisualStore;

    taskCreateVisualStore: TaskCreateVisualStore;

    tasks: TaskListModel[] = [];

    tasksLoading: boolean = false;

    metadataDefinitions: MetadataDefinition[] = [];

    metadataDefinitionsLoading: boolean = false;

    actionDefinitions: ActionDefinition[] = [];

    actionDefinitionsLoading: boolean = false;

    taskTypes: TaskType[] = [];

    taskStatuses: TaskStatus[] = [];

    allUsersFullNameResolver: { [id: string]: string } = {};

    activeUsersFullNameResolver: { [id: string]: string } = {};

    userProfiles: UserProfile[] = [];

    usersLoaded: boolean = false;

    loadingUsers: boolean = false;

    usersInProject: UserProfile[] = [];

    userProfilePictures: { [id: string]: string | undefined } = {};

    tasksCount: number;

    pageIsLoading: boolean = true;

    layoutIsLoading: boolean = true;

    tasksData: TasksData;

    private currentLoadTasksRequestId = 0;

    constructor(
        private tasksService: TaskService,
        private tasksTypesService: TaskTypesService,
        private projectsStore: ProjectsStore,
        tabsStore: TabsStore,
        private errorStore: ErrorStore,
        private adminService: GlobalAdministrationService,
        private actionService: ActionService,
        private userProfileStore: UserProfileStore,
        notificationStore: NotificationStore,
    ) {
        this.tasksGlobalViewVisualStore = new TaskViewVisualStore(
            tasksService,
            tabsStore,
            errorStore,
            userProfileStore,
            notificationStore,
            this,
            new ClientActionRunner(),
        );
        this.tasksFiltersVisualStore = new TasksFiltersVisualStore(this);
        this.tasksGridVisualStore = new TasksGridVisualStore(
            this,
            errorStore,
            tasksService,
        );
        this.taskCreateVisualStore = new TaskCreateVisualStore(
            projectsStore,
            errorStore,
            tasksService,
            tasksTypesService,
            adminService,
            this,
        );
        makeObservable<TasksRootStore, "loadAllUsers">(this, {
            metadataDefinitions: observable,
            metadataDefinitionsLoading: observable,
            actionDefinitions: observable,
            actionDefinitionsLoading: observable,
            taskTypes: observable,
            loadingUsers: observable,
            tasks: observable,
            tasksCount: observable,
            usersInProject: observable,
            layoutIsLoading: observable,
            loadTaskTypes: action.bound,
            columnsFilterDict: computed,
            projects: computed,
            allUsersFullNameResolver: observable,
            activeUsersFullNameResolver: observable,
            usersLoaded: observable,
            userProfilePictures: observable,
            tasksLoading: observable,
            pageIsLoading: observable,
            loadAllUsers: action.bound,
            updateField: action.bound,
            getUserProfilePicture: action.bound,
            loadUsersForProject: action.bound,
            loadActionDefinitions: action.bound,
            resetAllFilters: action.bound,
            setLayoutIsLoading: action.bound,
            setTasksIsLoading: action,
            setPageIsLoading: action,
            unsetAllColumnsFilters: action,
            isWidgetView: computed,
        });

        this.init();
        this.loadAllUsers();
        this.getTaskStatuses();
        this.loadActionDefinitions();
    }

    async init() {
        await this.projectsStore.init();
        this.getTaskListLayout();
    }

    get selectedProject() {
        return this.tasksGridVisualStore.selectedProject;
    }

    get taskTypesForCurrentProject() {
        return this.selectedProject === ALL_PROJECTS
            ? this.taskTypes
            : this.taskTypes.filter(
                  (t) => t.projectId === this.selectedProject,
              );
    }

    get taskStatusesForCurrentProject() {
        const types =
            this.selectedProject === ALL_PROJECTS
                ? this.taskTypes
                : this.taskTypes.filter(
                      (t) => t.projectId === this.selectedProject,
                  );
        return this.taskStatuses.filter((t) =>
            types.map((x) => x.id).includes(t.typeId),
        );
    }

    get columnsFilterDict() {
        return this.tasksFiltersVisualStore.columnsFilterDict;
    }

    get currentUserId() {
        return this.userProfileStore.userProfile?.userId;
    }

    get taskPreview() {
        return this.tasksGlobalViewVisualStore.taskPreview;
    }

    get projects() {
        return this.projectsStore.projects;
    }

    get isWidgetView() {
        return this.tasksGridVisualStore.isWidgetView;
    }

    getProjectName = (id: string) => {
        return this.projectsStore.projects.find((x) => x.id === id)!.name;
    };

    getTaskName = (id: string) => {
        return this.taskTypes.find((x) => x.id === id)?.name;
    };

    getTaskSatusesByProjectId(id: string) {
        const types = this.taskTypes.filter((t) => t.projectId === id);
        return this.taskStatuses.filter((t) =>
            types.map((x) => x.id).includes(t.typeId),
        );
    }

    getActionName(definitionId: string) {
        const def = this.actionDefinitions.find((x) => x.id === definitionId);
        return def ? (def.title ?? def.name) : "Unknown action";
    }

    setTasksIsLoading(val: boolean) {
        this.tasksLoading = val;
    }

    setPageIsLoading(val: boolean) {
        this.pageIsLoading = val;
    }

    setLayoutIsLoading(val: boolean) {
        this.layoutIsLoading = val;
    }

    async loadTasks(excludeFilters?: boolean) {
        try {
            // TODO: Do we need this check?
            if (this.projectsStore.projects) {
                const project =
                    this.tasksGridVisualStore.selectedProject === ALL_PROJECTS
                        ? null
                        : this.tasksGridVisualStore.selectedProject;
                let tasksData: TasksData;
                const sortIndex =
                    this.tasksGridVisualStore.sortOrder.order === "ascend"
                        ? 1
                        : -1;
                const resp = await this.tasksService.getTasks(
                    project,
                    this.tasksGridVisualStore.currentPage,
                    this.tasksGridVisualStore.pageSize,
                    this.tasksGridVisualStore.searchTerm || null,
                    !!excludeFilters,
                    sortIndex,
                    this.tasksGridVisualStore.sortOrder.field,
                );

                resp.map((r) => {
                    tasksData = r;
                    this.setTasks(tasksData);
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                }).mapErr((err: any) => this.errorStore.addError(err.data));
            } else {
                reaction(
                    () => this.projectsStore.isLoading,
                    (p, prev, r) => {
                        if (this.projectsStore.projects) {
                            this.loadTasks();
                            r.dispose();
                        }
                    },
                );
            }
        } catch (err) {
            console.error(err);
        }
    }

    async reloadTasks() {
        this.setTasksIsLoading(true);

        const gridStore = this.tasksGridVisualStore;
        const sortIndex = gridStore.sortOrder.order === "ascend" ? 1 : -1;

        let resp;

        if (gridStore.isWidgetView && !gridStore.currentWidgetValue) {
            // Grouped widget view
            resp = await this.tasksService.getTasksByGroupedWidgetId(
                gridStore.currentWidgetId!,
                this.tasksGridVisualStore.selectedProject,
                gridStore.currentPage,
                gridStore.pageSize,
                gridStore.searchTerm,
                gridStore.sortOrder.field,
                sortIndex,
                this.tasksFiltersVisualStore.prepareRequestFilters(),
            );
        } else if (gridStore.isWidgetView) {
            // Pie chart section view
            resp = await this.tasksService.getTasksByPieChartSection(
                gridStore.currentWidgetId!,
                this.tasksGridVisualStore.selectedProject,
                gridStore.currentWidgetValue!,
                gridStore.currentPage,
                gridStore.pageSize,
                gridStore.searchTerm,
                gridStore.sortOrder.field,
                sortIndex,
            );
        } else {
            const project =
                this.tasksGridVisualStore.selectedProject === ALL_PROJECTS
                    ? null
                    : this.tasksGridVisualStore.selectedProject;
            // Standard tasks view
            resp = await this.tasksService.getTasks(
                project,
                gridStore.currentPage,
                gridStore.pageSize,
                gridStore.searchTerm || null,
                false,
                sortIndex,
                gridStore.sortOrder.field,
            );
        }

        return this.handleTasksResponse(resp);
    }

    private handleTasksResponse(resp: ResultApi<TasksData>) {
        return resp
            .map((r: TasksData) => {
                runInAction(() => {
                    this.setTasks(r);
                    this.setTasksIsLoading(false);
                });
            })
            .mapErr((err) => {
                this.errorStore.addError(err.data);
                this.setTasksIsLoading(false);
            });
    }

    setColumnsFilter() {
        this.tasksFiltersVisualStore.setColumnsFilter(this.tasksData);
    }

    async loadMetadata(projectId: string) {
        this.metadataDefinitionsLoading = true;
        const result =
            await this.adminService.getMetadataDefinitionsForProject(projectId);
        result
            .map((r) => {
                runInAction(() => {
                    this.metadataDefinitions = r;
                });
            })
            .mapErr((err) => this.errorStore.addError(err.data));

        this.metadataDefinitionsLoading = false;
    }

    async loadActionDefinitions() {
        runInAction(() => {
            this.actionDefinitionsLoading = true;
        });

        const resp = await this.actionService.getActionDefinitions();
        resp.map((r) => {
            runInAction(() => {
                this.actionDefinitions = r;
            });
        }).mapErr((err) => this.errorStore.addBasicError(err));

        runInAction(() => {
            this.actionDefinitionsLoading = false;
        });
    }

    async loadTaskTypes() {
        this.tasksGlobalViewVisualStore.taskTypesLoading = true;
        const resp = await this.tasksTypesService.getTaskTypesForProject();
        resp.map((t) => {
            runInAction(() => {
                this.taskTypes = t;
            });
        }).mapErr((err) => this.errorStore.addError(err.data));

        runInAction(() => {
            this.tasksGlobalViewVisualStore.taskTypesLoading = false;
        });
    }

    async loadUsersForProject(projectId: string) {
        this.loadingUsers = true;
        const resp = await this.tasksService.getUsersInProject(projectId);
        resp.map((users: UserProfile[]) => {
            runInAction(() => {
                this.usersInProject = users;
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).mapErr((err: any) => this.errorStore.addError(err.data));
        runInAction(() => (this.loadingUsers = false));
    }

    async getUserProfilePicture(userId: string) {
        if (userId in this.userProfilePictures) {
            return;
        }
        try {
            runInAction(() => {
                this.userProfilePictures[userId] = undefined;
            });

            const response =
                await this.tasksService.getUserProfilePicture(userId);
            response
                .map((blob: Blob) => {
                    runInAction(() => {
                        if (blob.size > 0) {
                            this.userProfilePictures[userId] =
                                URL.createObjectURL(blob);
                        }
                    });
                })
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .mapErr((err: any) => this.errorStore.addError(err.data));
        } catch (err) {
            this.errorStore.addBasicError(err);
        }
    }

    async getTaskStatuses() {
        const resp = await this.tasksService.getTaskStatuses();
        resp.map((r) => {
            runInAction(() => {
                this.taskStatuses = r;
            });
        });
    }

    async getTasksByGroupedWidgetId(
        id: string,
        projectId: string,
        page: number,
        sortField: string,
        sortIndex: string,
    ) {
        this.setTasksIsLoading(true);
        const currentRequestId = ++this.currentLoadTasksRequestId;
        const resp = await this.tasksService.getTasksByGroupedWidgetId(
            id,
            projectId,
            page,
            this.tasksGridVisualStore.pageSize,
            this.tasksGridVisualStore.searchTerm,
            sortField,
            sortIndex === "ascend" ? 1 : -1,
            this.tasksFiltersVisualStore.prepareRequestFilters(),
        );
        if (currentRequestId !== this.currentLoadTasksRequestId) {
            return; // Ignore outdated request to avoid raice condition
        }
        return resp
            .map((r) => {
                runInAction(() => {
                    this.setTasks(r);
                    this.tasksFiltersVisualStore.setColumnsFilter(r);
                    this.tasksGridVisualStore.setCurrentWidgetName(
                        r.widgetName!,
                    );
                    this.setTasksIsLoading(false);
                });
            })
            .mapErr((err) => {
                this.errorStore.addError(err.data);
                this.setTasksIsLoading(false);
            });
    }

    async getTasksByPieChartSection(
        id: string,
        projectId: string,
        value: string,
        page: number,
        sortField: string,
        sortIndex: string,
    ) {
        this.setTasksIsLoading(true);
        this.unsetAllColumnsFilters();
        const currentRequestId = ++this.currentLoadTasksRequestId;
        const resp = await this.tasksService.getTasksByPieChartSection(
            id,
            projectId,
            value,
            page,
            this.tasksGridVisualStore.pageSize,
            this.tasksGridVisualStore.searchTerm,
            sortField,
            sortIndex === "ascend" ? 1 : -1,
        );
        if (currentRequestId !== this.currentLoadTasksRequestId) {
            return; // Ignore outdated request to avoid raice condition
        }
        return resp
            .map((r) => {
                runInAction(() => {
                    this.setTasks(r);
                    this.tasksFiltersVisualStore.setColumnsFilter(r);
                    this.tasksGridVisualStore.setCurrentWidgetName(
                        r.widgetName!,
                    );
                    this.setTasksIsLoading(false);
                });
            })
            .mapErr((err) => {
                this.errorStore.addError(err.data);
                this.setTasksIsLoading(false);
            });
    }

    async getTasksByActivityType(
        acitivityType: string,
        projectId: string,
        period: string,
        page: number,
        sortField: string,
        sortIndex: string,
    ) {
        this.setTasksIsLoading(true);
        const currentRequestId = ++this.currentLoadTasksRequestId;
        this.unsetAllColumnsFilters();
        const resp = await this.tasksService.getTasksByActivityType(
            acitivityType,
            projectId,
            period,
            page,
            this.tasksGridVisualStore.pageSize,
            this.tasksGridVisualStore.searchTerm,
            sortField,
            sortIndex === "ascend" ? 1 : -1,
        );
        if (currentRequestId !== this.currentLoadTasksRequestId) {
            return; // Ignore outdated request to avoid raice condition
        }
        return resp.map((r) => {
            runInAction(() => {
                this.setTasks(r);
                this.tasksFiltersVisualStore.setColumnsFilter(r);
                this.tasksGridVisualStore.setCurrentWidgetName(
                    "Last activities",
                );
                this.setTasksIsLoading(false);
            });
        });
    }

    unsetAllColumnsFilters() {
        this.tasksFiltersVisualStore.unsetAllColumnsFilters();
    }

    async resetAllFilters() {
        const currentRequestId = ++this.currentLoadTasksRequestId;
        const projectId =
            this.selectedProject === ALL_PROJECTS ? null : this.selectedProject;

        this.tasksGridVisualStore.unSelectAllRows();
        const resp = await this.tasksService.resetAllColumnFilters(
            projectId,
            this.tasksGridVisualStore.pageSize,
            this.tasksGridVisualStore.currentPage,
            this.tasksGridVisualStore.searchTerm,
        );
        if (currentRequestId !== this.currentLoadTasksRequestId) {
            return; // Ignore outdated request to avoid raice condition
        }
        return resp
            .map((t) => {
                this.setTasks(t);
                this.unsetAllColumnsFilters();
            })
            .mapErr((err) => this.errorStore.addError(err.data));
    }

    async loadAllUsers() {
        const resp = await this.tasksService.getAllUsers();

        resp.map((users: UserProfile[]) => {
            this.userProfiles = users;
            const distinctUsers = users.filter(this.distinctUser);

            runInAction(() => {
                for (const user of distinctUsers) {
                    this.allUsersFullNameResolver[user.userId!] =
                        this.formatUserDisplayName(user);
                }
                for (const user of distinctUsers.filter((u) => !u.isDeleted)) {
                    this.activeUsersFullNameResolver[user.userId!] =
                        this.formatUserDisplayName(user);
                }
                this.usersLoaded = true;
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }).mapErr((err: any) => this.errorStore.addError(err.data));
    }

    getUserDisplayName(userId?: string | null, fallback?: string | null) {
        if (!userId) {
            return fallback || "";
        }

        return (
            this.allUsersFullNameResolver[userId] ||
            this.formatUserDisplayName(
                this.userProfiles.find((user) => user.userId === userId),
            ) ||
            fallback ||
            userId
        );
    }

    private formatUserDisplayName(user?: UserProfile) {
        if (!user) {
            return "";
        }

        return (
            [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
            user.userName
        );
    }

    async getTaskListLayout() {
        this.setLayoutIsLoading(true);
        const resp = await this.tasksService.getTaskListLayoutForUser();
        resp.map((layout) => {
            this.tasksGridVisualStore.setTaskListLayout(layout);
        }).mapErr((err) => this.errorStore.addError(err.data));
        this.setLayoutIsLoading(false);
    }

    async updateColumnFilters(
        projectId: string | null,
        filters: ColumnsFilter[],
    ) {
        this.tasksGridVisualStore.unSelectAllRows();
        const resp = await this.tasksService.updateColumnFilters(
            projectId,
            this.tasksGridVisualStore.pageSize,
            this.tasksGridVisualStore.currentPage,
            filters,
            this.tasksGridVisualStore.searchTerm,
            undefined,
            undefined,
            this.tasksGridVisualStore.currentWidgetId,
            this.tasksGridVisualStore.currentWidgetValue,
        );
        resp.map((t) => {
            this.setTasks(t);
        }).mapErr((err) => this.errorStore.addError(err.data));
    }

    async updateField(
        taskId: string,
        fieldName: TaskUpdateFieldName,
        value: unknown,
        callback: (() => void) | undefined = undefined,
        ignoreTasksReload: boolean = false,
    ) {
        if (fieldName) {
            const fields: { [fieldName: string]: unknown } = {};
            fields[fieldName] = value || null;
            // TODO: return updated task and update list
            const resp = await this.tasksService.updateFields(fields, taskId);
            await resp
                .asyncMap(async () => {
                    runInAction(async () => {
                        if (!ignoreTasksReload) {
                            this.reloadTasks();
                        }
                        if (callback) {
                            callback();
                        }
                    });
                })
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .mapErr((err: any) => this.errorStore.addError(err.data));
            if (resp.isErr()) {
                return false;
            }
        }
        return true;
    }

    togglePreview(task: TaskListModel | undefined) {
        this.tasksGlobalViewVisualStore.togglePreview(task?.id);
    }

    setPinnedTasks(tasks: PinnedTask[]) {
        this.tasksGlobalViewVisualStore.setPinnedTasks(tasks);
    }

    setAssignedUserInPreview(userId: string, taskIds: string[]) {
        this.tasksGlobalViewVisualStore.setAssignedUserInPreview(
            userId,
            taskIds,
        );
    }

    setTasks(tasksData: TasksData) {
        runInAction(() => {
            const { tasks, tasksCount } = tasksData;
            this.tasksCount = tasksCount;
            this.tasks = tasks;
            this.tasksData = tasksData;
        });
    }

    setTasksList(tasks: TaskModel[]) {
        this.tasks = tasks;
    }

    unSelectRow(taskId: string) {
        this.tasksGridVisualStore.unSelectRow(taskId);
    }

    removeSubscriptionsFromsGlobalViewStore() {
        this.tasksGlobalViewVisualStore.removeSubscriptions();
    }

    updateTaskMetadata(message: MetadataPushMessage) {
        const task = this.tasks.find((t) => t.id === message.taskId);
        if (task) {
            task.metadata = message.fields;
        }
    }

    getGlobalTaskPreview() {
        return this.tasksGlobalViewVisualStore.taskPreview;
    }

    private distinctUser(
        value: UserProfile,
        index: number,
        self: Array<UserProfile>,
    ): boolean {
        const user = self.find((u) => u.userId === value.userId);
        return self.indexOf(user!) === index;
    }
}
